import { useState } from 'react';
import { AppShell } from '@/components/AppShell';
import { useListSites, useCreateSite, getListSitesQueryKey } from '@workspace/api-client-react';
import { Plus, Globe, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

export default function SitesPage() {
  const { data: sites, isLoading } = useListSites();
  const createSite = useCreateSite();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  
  const [open, setOpen] = useState(
    () => new URLSearchParams(window.location.search).get('add') === '1',
  );
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !domain.trim()) return;

    createSite.mutate(
      { data: { name: name.trim(), domain: domain.trim() } },
      {
        onSuccess: (createdSite) => {
          queryClient.invalidateQueries({ queryKey: getListSitesQueryKey() });
          toast({ title: 'Website added', description: 'Next, follow the installation guide.' });
          setOpen(false);
          setName('');
          setDomain('');
          setLocation(`/sites/${createdSite.id}`);
        },
        onError: () => {
          toast({ title: 'Failed to add site', variant: 'destructive' });
        },
      }
    );
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'verified':
        return { icon: CheckCircle2, color: 'text-chart-1', bgColor: 'bg-chart-1/10', label: 'Verified' };
      case 'pending':
        return { icon: Clock, color: 'text-chart-3', bgColor: 'bg-chart-3/10', label: 'Pending' };
      default:
        return { icon: AlertCircle, color: 'text-muted-foreground', bgColor: 'bg-muted', label: 'Not Installed' };
    }
  };

  return (
    <AppShell>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-sans font-bold tracking-tight text-3xl md:text-4xl text-foreground mb-2">Sites</h1>
            <p className="text-muted-foreground">Manage your websites and accessibility widgets</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-site">
                <Plus className="w-4 h-4 mr-2" />
                Add Site
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add your website</DialogTitle>
                <DialogDescription>
                  Tell us which website you want to use. We’ll show you installation steps for your website builder next.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="site-name">What do you call your website?</Label>
                  <Input
                    id="site-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Meridian Homes"
                    required
                    data-testid="input-site-name"
                  />
                </div>
                <div>
                  <Label htmlFor="site-domain">Website address</Label>
                  <Input
                    id="site-domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="meridianhomes.com"
                    required
                    data-testid="input-site-domain"
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">For example: meridianhomes.com</p>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createSite.isPending} data-testid="button-submit-site">
                    {createSite.isPending ? 'Adding website...' : 'Continue to installation'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="p-6 rounded-lg border border-border bg-card animate-pulse">
                <div className="h-40" />
              </div>
            ))}
          </div>
        ) : !sites || sites.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">No sites yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Add your first site to start monitoring accessibility and embed the widget.
            </p>
            <Button onClick={() => setOpen(true)} data-testid="button-add-first-site">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Site
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((site, i) => {
              const statusConfig = getStatusConfig(site.installStatus);
              const StatusIcon = statusConfig.icon;
              return (
                <Link
                  key={site.id}
                  href={`/sites/${site.id}`}
                  className={`p-6 rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)] hover:shadow-md hover:border-primary/20 transition-all duration-300 block stagger-item`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`site-card-${site.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                      <Globe className="w-6 h-6 text-primary" />
                    </div>
                    <div className={`px-2.5 py-1 rounded-md ${statusConfig.bgColor} flex items-center gap-1.5`}>
                      <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                      <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">{site.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{site.domain}</p>
                  <div className="flex items-center justify-between text-sm pt-4 border-t border-card-border">
                    <div>
                      <span className="text-muted-foreground">Score:</span>{' '}
                      <span className="font-medium text-foreground">{site.latestScore ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Visits:</span>{' '}
                      <span className="font-medium text-foreground">{site.monthlyVisits.toLocaleString()}</span>
                    </div>
                  </div>
                  {site.lastSeenAt && (
                    <div className="text-xs text-muted-foreground mt-3">
                      Last seen {formatDistanceToNow(new Date(site.lastSeenAt), { addSuffix: true })}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
